import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import './Admin.css';

export default function RiderManagement() {
  const { api } = useContext(AuthContext);

  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    name: '',
    email: '',
    phone: '',
    password: '',
    address: '',
    city: '',
    active: true
  });

  useEffect(() => {
    fetchRiders();
  }, []);

  const fetchRiders = async () => {
    try {
      setError('');
      const response = await api.get('/riders');
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.riders || response.data?.data || []);
      setRiders(data);
    } catch (err) {
      console.error('Fetch riders error:', err);
      setError('Failed to load riders');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchRiders();
  };

  const openAddModal = () => {
    setModalMode('add');
    setFormData({
      id: null,
      name: '',
      email: '',
      phone: '',
      password: '',
      address: '',
      city: '',
      active: true
    });
    setModalOpen(true);
  };

  const openEditModal = (rider) => {
    setModalMode('edit');
    setFormData({
      id: rider.id,
      name: rider.name || '',
      email: rider.email || '',
      phone: rider.phone || '',
      password: '',
      address: rider.address || '',
      city: rider.city || '',
      active: rider.active !== undefined ? Boolean(rider.active) : true
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const validate = () => {
    if (!formData.name.trim()) return 'Name is required';
    if (!formData.phone.trim()) return 'Phone is required';

    if (modalMode === 'add') {
      if (!formData.email.trim()) return 'Email is required';
      if (!formData.password.trim()) return 'Password is required';
      if (formData.password.length < 6) return 'Password must be at least 6 characters';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errorMsg = validate();
    if (errorMsg) {
      alert(errorMsg);
      return;
    }

    setSaving(true);

    try {
      if (modalMode === 'add') {
        const payload = {
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          password: formData.password.trim(),
          role: 'rider',
          address: formData.address.trim() || null,
          city: formData.city.trim() || null,
          active: formData.active
        };

        await api.post('/riders', payload);
        alert('Rider added successfully!');
      } else {
        const payload = {
          name: formData.name.trim(),
          email: formData.email.trim() || null,
          phone: formData.phone.trim(),
          address: formData.address.trim() || null,
          city: formData.city.trim() || null,
          active: formData.active
        };

        if (formData.password.trim()) {
          payload.password = formData.password.trim();
        }

        await api.put(`/riders/${formData.id}`, payload);
        alert('Rider updated successfully!');
      }

      setModalOpen(false);
      fetchRiders();
    } catch (err) {
      console.error('Save rider error:', err);
      alert(err.response?.data?.message || 'Failed to save rider');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (rider) => {
    try {
      await api.put(`/riders/${rider.id}/status`, {
        active: !rider.active
      });
      fetchRiders();
    } catch (err) {
      console.error('Toggle status error:', err);
      alert(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleDelete = async (rider) => {
    if (!window.confirm(`Delete rider ${rider.name}?`)) return;

    try {
      await api.delete(`/riders/${rider.id}`);
      fetchRiders();
      alert('Rider deleted successfully!');
    } catch (err) {
      console.error('Delete rider error:', err);
      alert(err.response?.data?.message || 'Failed to delete rider');
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading riders..." />;
  }

  return (
    <div className="admin-rider-management">
      <div className="container">
        <div className="page-header">
          <div>
            <h1 className="page-title">
              <span className="title-icon">🏍️</span>
              Rider Management
            </h1>
            <p className="page-subtitle">Total Riders: {riders.length}</p>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-outline btn-sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
            </button>
            <button className="btn btn-primary" onClick={openAddModal}>
              ➕ Add Rider
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span className="alert-text">{error}</span>
          </div>
        )}

        {riders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏍️</div>
            <h2 className="empty-state-title">No riders found</h2>
            <p className="empty-state-text">Start by adding your first rider</p>
            <button className="btn btn-primary" onClick={openAddModal}>
              ➕ Add Rider
            </button>
          </div>
        ) : (
          <div className="riders-grid">
            {riders.map((rider) => (
              <div key={rider.id} className="rider-card">
                <div className="rider-header">
                  <div className="rider-info">
                    <h3 className="rider-name">{rider.name}</h3>
                    <p className="rider-email">{rider.email}</p>
                  </div>
                  <span
                    className={`status-badge ${
                      rider.active ? 'active' : 'inactive'
                    }`}
                  >
                    {rider.active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="rider-details">
                  <div className="detail-row">
                    <span className="detail-label">📞 Phone:</span>
                    <span className="detail-value">{rider.phone}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">📍 Address:</span>
                    <span className="detail-value">{rider.address || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">🏙️ City:</span>
                    <span className="detail-value">{rider.city || '—'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">🆔 ID:</span>
                    <span className="detail-value">{rider.id}</span>
                  </div>
                </div>

                <div className="rider-actions">
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => openEditModal(rider)}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className={`btn btn-sm ${
                      rider.active ? 'btn-secondary' : 'btn-primary'
                    }`}
                    onClick={() => handleToggleStatus(rider)}
                  >
                    {rider.active ? '⏸️ Deactivate' : '▶️ Activate'}
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(rider)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {modalMode === 'add' ? '➕ Add Rider' : '✏️ Edit Rider'}
              </h2>
              <button className="modal-close" onClick={closeModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Rider name"
                  disabled={saving}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  Email {modalMode === 'add' ? '*' : ''}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="rider@email.com"
                  disabled={saving}
                  required={modalMode === 'add'}
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone *</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="03XX-XXXXXXX"
                  disabled={saving}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">
                  Password {modalMode === 'add' ? '*' : '(optional)'}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={
                    modalMode === 'add'
                      ? 'Minimum 6 characters'
                      : 'Leave empty to keep same'
                  }
                  disabled={saving}
                  required={modalMode === 'add'}
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <input
                  id="address"
                  name="address"
                  type="text"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Address"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleChange}
                    disabled={saving}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`btn btn-primary ${saving ? 'btn-loading' : ''}`}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-small"></span>
                      Saving...
                    </>
                  ) : modalMode === 'add' ? (
                    'Add Rider'
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}